import { useState, useEffect } from "react";
import {
  HiOutlineClock,
  HiOutlineUserPlus,
  HiOutlineFolder,
  HiOutlineClipboardDocumentList,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineStar,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { cn } from "../../../lib/cn";
import { useRecentActivities } from "../../../lib/react-query/activities";
import Pagination from "../../shared/Pagination";

const PAGE_SIZE = 5;

const ACTIVITY_META = {
  project_created: { label: "Project", Icon: HiOutlineFolder, pill: "bg-secondary/10 text-secondary-dark" },
  project_updated: { label: "Project", Icon: HiOutlineFolder, pill: "bg-secondary/10 text-secondary-dark" },
  project_deleted: { label: "Project", Icon: HiOutlineFolder, pill: "bg-danger/10 text-danger" },
  task_created: { label: "Task", Icon: HiOutlineClipboardDocumentList, pill: "bg-warning/10 text-warning" },
  task_updated: { label: "Task", Icon: HiOutlineClipboardDocumentList, pill: "bg-primary/5 text-primary" },
  task_deleted: { label: "Task", Icon: HiOutlineClipboardDocumentList, pill: "bg-danger/10 text-danger" },
  member_added: { label: "Team", Icon: HiOutlineUserGroup, pill: "bg-success/10 text-success" },
  member_removed: { label: "Team", Icon: HiOutlineUserGroup, pill: "bg-danger/10 text-danger" },
  partner_request_sent: { label: "Connection", Icon: HiOutlineUserPlus, pill: "bg-primary/5 text-primary" },
  partner_request_accepted: { label: "Connection", Icon: HiOutlineUserPlus, pill: "bg-success/10 text-success" },
  partner_request_rejected: { label: "Connection", Icon: HiOutlineUserPlus, pill: "bg-danger/10 text-danger" },
  contract_created: { label: "Contract", Icon: HiOutlineDocumentText, pill: "bg-primary/10 text-primary" },
  contract_updated: { label: "Contract", Icon: HiOutlineDocumentText, pill: "bg-primary/10 text-primary" },
  review_request_sent: { label: "Review", Icon: HiOutlineStar, pill: "bg-secondary/10 text-secondary-dark" },
  review_submitted: { label: "Review", Icon: HiOutlineStar, pill: "bg-success/10 text-success" },
};

const DEFAULT_META = { label: null, Icon: null, pill: "bg-surface text-mediumgrey" };

const AVATAR_PALETTE = [
  "bg-secondary/10 text-secondary-dark",
  "bg-success/10 text-success",
  "bg-primary/5 text-primary",
  "bg-warning/10 text-warning",
  "bg-chat-user/10 text-chat-user",
  "bg-danger/10 text-danger",
  "bg-surface text-darkgrey",
];

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};

const getSubtitle = (activity) => {
  const meta = activity.metadata || {};
  return meta.project_title || meta.project_name || null;
};

const formatActivityTime = (dateString) => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const PLACEHOLDER_COUNT = 3;

const CardShell = ({ children }) => (
  <div className="bg-white rounded-2xl p-6 border border-surface-border">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-lg font-semibold text-primary">Recent Activity</h2>
      <HiOutlineClock className="w-5 h-5 text-mediumgrey" />
    </div>
    {children}
  </div>
);

const RecentActivity = ({ showFetch = true }) => {
  const { data: fetchedActivities = [], isLoading } = useRecentActivities(30, 30, showFetch);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(fetchedActivities.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const startIdx = (page - 1) * PAGE_SIZE;
  const visibleActivities = fetchedActivities.slice(startIdx, startIdx + PAGE_SIZE);

  if (isLoading) {
    return (
      <CardShell>
        <div>
          {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
            <div
              key={i}
              className={cn("relative flex gap-5", i < PLACEHOLDER_COUNT - 1 && "pb-8")}
            >
              <div className="relative flex flex-col items-center">
                {i < PLACEHOLDER_COUNT - 1 && (
                  <div className="absolute top-10 h-full w-px bg-surface-hover" />
                )}
                <div className="relative z-10 size-10 rounded-full bg-surface-hover motion-safe:animate-pulse" />
              </div>
              <div className="flex flex-1 items-start justify-between gap-4 pt-1.5">
                <div className="space-y-2">
                  <div className="h-3.5 w-36 rounded-full bg-surface-hover motion-safe:animate-pulse" />
                  <div className="h-3 w-56 rounded-full bg-surface-hover motion-safe:animate-pulse" />
                </div>
                <span className="h-3 w-10 shrink-0 rounded-full bg-surface-hover motion-safe:animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </CardShell>
    );
  }

  if (!fetchedActivities.length) {
    return (
      <CardShell>
        <div className="flex flex-col items-center justify-center text-center py-6">
          <div className="w-10 h-10 rounded-full bg-surface flex items-center justify-center mb-2">
            <HiOutlineSparkles className="w-4 h-4 text-mediumgrey" />
          </div>
          <p className="text-sm font-medium text-darkgrey">No activity yet</p>
          <p className="text-xs text-mediumgrey mt-1 max-w-xs">
            Updates from your project will appear here as the team makes progress.
          </p>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div>
        {visibleActivities.map((activity, index) => {
          const meta = ACTIVITY_META[activity.activity_type] || DEFAULT_META;
          const timestamp = formatActivityTime(activity.created_at);
          const subtitle = getSubtitle(activity);
          const initials = getInitials(activity.user_name);
          const isLast = index === visibleActivities.length - 1;

          return (
            <div
              key={activity.id}
              className={cn(
                "relative flex gap-5",
                !isLast && "pb-8",
              )}
            >
              <div className="relative flex flex-col items-center">
                {!isLast && (
                  <div className="absolute top-10 h-full w-px bg-surface-hover" />
                )}
                <div
                  className={cn(
                    "relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    getAvatarColor(activity.user_name),
                  )}
                >
                  {initials}
                  {meta.Icon && (
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full ring-2 ring-white",
                        meta.pill,
                      )}
                    >
                      <meta.Icon className="size-2.5" />
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-1 items-start justify-between gap-4 pt-1 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm text-darkgrey leading-snug truncate">
                    {activity.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {meta.label && (
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider leading-none",
                          meta.pill,
                        )}
                      >
                        {meta.label}
                      </span>
                    )}
                    {subtitle && (
                      <span className="text-xs text-mediumgrey truncate">
                        {subtitle}
                      </span>
                    )}
                  </div>
                </div>
                {timestamp && (
                  <span className="text-xs text-mediumgrey shrink-0 tabular-nums mt-0.5">
                    {timestamp}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={fetchedActivities.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </CardShell>
  );
};

export default RecentActivity;
